import { createContext } from "react"
import ErrorBoundary from "../../components/ErrorBoundary"

import {
	ArrowBack as ArrowBackIcon,
	VisibilityOutlined as VisibilityOutlinedIcon,
	ContactPhoneOutlined as ContactPhoneOutlinedIcon,
	CreditCardOutlined as CreditCardOutlinedIcon,
	Inventory2Outlined as Inventory2OutlinedIcon,
	AdminPanelSettings as AdminPanelSettingsIcon,
	AssessmentOutlined as AssessmentOutlinedIcon,
	OpenInNewOutlined as OpenInNewOutlinedIcon,
	CategoryOutlined as CategoryOutlinedIcon,
} from '@mui/icons-material'

import SettingsBase from "./base"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import Phones from "./phones"
import Admins from "./admins"
import Payments from './payments'
import RestrictedWordsPage from "./restricted"
import ProductsPage from "./products"
import ReportsPage from "./reports"
import CategoriesPage from "./categories"
import { KWIK_SCOUT_BEST_PRACTICES_GUIDE_URL } from "../../config"

export const settingsContext = createContext()

export const SETTINGS_PATHS = {
	base: '/configuracoes',
	phones: '/configuracoes/telefones',
	admins: '/configuracoes/gestores',
	payments: '/configuracoes/payments',
	restricted: '/configuracoes/palavras-restritas',
	products: '/configuracoes/produtos-servicos',
	reports: '/configuracoes/relatorios',
	categories: '/configuracoes/categorias',
}

const SETTINGS_PAGES = {
	base: { component: <SettingsBase />, title: 'Configurações', back: 'Página inicial', icon: <></> },
	telefones: { component: <Phones />, title: 'Telefones monitorados', back: 'Configurações da conta', icon: <ContactPhoneOutlinedIcon />, guideUrl: KWIK_SCOUT_BEST_PRACTICES_GUIDE_URL },
	phones: { component: <Phones />, title: 'Telefones monitorados', back: 'Configurações da conta', icon: <ContactPhoneOutlinedIcon />, guideUrl: KWIK_SCOUT_BEST_PRACTICES_GUIDE_URL },
	pagamentos: { component: <Payments />, title: 'Pagamentos e assinatura', back: 'Configurações da conta', icon: <CreditCardOutlinedIcon /> },
	payments: { component: <Payments />, title: 'Pagamentos e assinatura', back: 'Configurações da conta', icon: <CreditCardOutlinedIcon /> },
	'palavras-restritas': { component: <RestrictedWordsPage />, title: 'Palavras-chave', back: 'Configurações da conta', icon: <VisibilityOutlinedIcon /> },
	restricted: { component: <RestrictedWordsPage />, title: 'Palavras-chave', back: 'Configurações da conta', icon: <VisibilityOutlinedIcon /> },
	'produtos-servicos': { component: <ProductsPage />, title: 'Produtos e serviços', back: 'Configurações da conta', icon: <Inventory2OutlinedIcon /> },
	products: { component: <ProductsPage />, title: 'Produtos e serviços', back: 'Configurações da conta', icon: <Inventory2OutlinedIcon /> },
	'relatorios': { component: <ReportsPage />, title: 'Relatórios', back: 'Configurações da conta', icon: <AssessmentOutlinedIcon /> },
	reports: {	component: <ReportsPage />, title: 'Relatórios', back: 'Configurações da conta', icon: <AssessmentOutlinedIcon /> },
	gestores: { component: <Admins />, title: "Gestores", back: "Configurações da conta", icon: <AdminPanelSettingsIcon /> },
	admins: { component: <Admins />, title: "Gestores", back: "Configurações da conta", icon: <AdminPanelSettingsIcon /> },
	'categorias': { component: <CategoriesPage />, title: 'Categorias', back: 'Configurações da conta', icon: <CategoryOutlinedIcon /> },
	categories: { component: <CategoriesPage />, title: 'Categorias', back: 'Configurações da conta', icon: <CategoryOutlinedIcon /> },
}

const Settings = ({ openOn }) => {

	const navigate = useNavigate()
	const location = useLocation()
	const previousPath = location.state?.from
	const { '*': subpage } = useParams()
	const currentPage = subpage || openOn || 'base'


	const handleBack = () => {
		currentPage === 'base' ? navigate('/home') : navigate(-1)
	}

	const component = SETTINGS_PAGES[currentPage] || SETTINGS_PAGES.base

	const changeLocation = (newlocation) => {
		if (!newlocation) {
			return
		}

		if (String(newlocation).startsWith('/')) {
			navigate(newlocation, { state: { from: 'settings' } })
			return
		}

		navigate(`/configuracoes/${newlocation}`, { state: { from: 'settings' } })
	}

	return (
		<div className="scout-settings-page" >
			<button type="button" className="scout-settings-back" onClick={handleBack}>
				<ArrowBackIcon />
				<span>{previousPath === 'settings' ? component?.back : 'Voltar'}</span>
			</button>
			<settingsContext.Provider value={{ changeLocation }}>
				<div className="scout-settings-shell">
					<header className="scout-settings-header">
						<h1>{component?.title}</h1>
						{component?.guideUrl ? (
							<a
								className="scout-settings-header__guide-link"
								href={component.guideUrl}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Abrir guia de boas práticas do Kwik Scout"
							>
								<span>Guia de boas práticas</span>
								<OpenInNewOutlinedIcon fontSize="small" />
							</a>
						) : null}
					</header>

					{component?.component}
				</div>
			</settingsContext.Provider>
		</div >)
}

const SettingsWithBoundary = (props) => (
    <ErrorBoundary
        componentName="Settings"
        fullScreen
        showReload
        logToBackend
        title="Erro nas configurações"
        message="Ocorreu um erro inesperado nesta seção de configurações. Tente recarregar."
    >
        <Settings {...props} />
    </ErrorBoundary>
)

export default SettingsWithBoundary
